import { BackButton } from "@publicPortal/components/BackButton/BackButton";
import Board from "@publicPortal/components/board/Board";
import ResponsiveAppBar from "@publicPortal/components/responsiveAppBar/ResponsiveAppBar";
import { RecordDetailsTabNames } from "@publicPortal/utils/enums";
import React, { memo, useCallback, useEffect, useState } from "react";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import BuildingRegulationTabs, { BuildingRegulationTabNames, PublicBuildingRegulationTabs } from "./BuildingRegulationTabs";
import BuildingRegulationsService from "@publicPortal/services/BuildingRegulationsService";
import { useParams } from "react-router-dom";
import BrSummaryTabContent from "./tabContent/BrSummaryTabContent";
import BrDocumentsTabContent from "./tabContent/BrDocumentsTabContent";
import { IBuildingRegulationsDetails } from "@shared/publicportal/contracts/IBuildingRegulationsDetails";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";
import { MobileTabPicker } from "@publicPortal/components/mobileTabPicker/MobileTabPicker";

export const buildingRegulationDetailsState = atom({
    key: "buildingRegulationDetailsState",
    default: {} as { [x: string]: RecordDetailsTabNames },
});
const service = new BuildingRegulationsService();

const BuildingRegulation = memo(() => {
    const { id } = useParams();
    const [bcApp, setBcApp] = useState<IBuildingRegulationsDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const { isMobile } = useDeviceDetect();
    const [currentTab, setCurrentTab] = useRecoilState(buildingRegulationDetailsState);
    const currentApplicationTab: RecordDetailsTabNames = bcApp?.id ? currentTab[bcApp.id] || RecordDetailsTabNames.Summary : RecordDetailsTabNames.Summary;
    const onTabSelect = useCallback(
        (newTab: string) => {
            if (!bcApp?.id) {
                return;
            }
            setCurrentTab((prev) => ({ ...prev, [bcApp.id]: newTab as RecordDetailsTabNames }));
        },
        [bcApp?.id, setCurrentTab]
    );

    useEffect(() => {
        setLoading(true);
        service.getBcAppById(Number(id)).then((response) => {
            if (!response) {
                setLoading(false);
                return;
            }
            setBcApp(response);
            setLoading(false);
        });
    }, [id]);

    if ((!bcApp || !bcApp.id) && !loading) {
        return (
            <>
                <ResponsiveAppBar />
                <Board headerText="Building Regulation not found" loadingHeaderText={loading} size="big">
                    <BackButton />
                </Board>
            </>
        );
    }

    return (
        <>
            <ResponsiveAppBar />
            <Board size="big" headerText={"Building Regulation " + (bcApp?.name || "(none)")} loadingHeaderText={loading}>
                <BackButton />
                {isMobile ? (
                    <MobileTabPicker label="Current Tab" currentTab={currentApplicationTab} tabs={PublicBuildingRegulationTabs} onTabSelect={onTabSelect} />
                ) : (
                    <BuildingRegulationTabs recordId={bcApp?.id} />
                )}
                <BuildingRegulationTabContent bcApp={bcApp} loading={loading} />
            </Board>
        </>
    );
});

const BuildingRegulationTabContent: React.FC<{ bcApp?: IBuildingRegulationsDetails | null; loading: boolean }> = memo(({ bcApp, loading }) => {
    const currentTab = useRecoilValue(buildingRegulationDetailsState);
    const currentApplicationTab: RecordDetailsTabNames = bcApp?.id ? currentTab[bcApp.id] || RecordDetailsTabNames.Summary : RecordDetailsTabNames.Summary;

    switch (currentApplicationTab) {
        case BuildingRegulationTabNames.Summary:
            return <BrSummaryTabContent bcApp={bcApp} loading={loading} />;
        case BuildingRegulationTabNames.Documents:
            return <BrDocumentsTabContent appId={bcApp?.id} />;
        default:
            return null;
    }
});

export default BuildingRegulation;
